// Asientos contables (libro diario). Contabilidad → Movimientos → Asientos.
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Eye, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useBorradorPantalla } from '@/lib/hooks';
import { Imprimible, PrintButton } from '@/components/print/DocPrint';
import { BuscadorCuenta } from '@/components/ui/BuscadorCuenta';
import { PanelIntegrado, useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { useTranslation } from 'react-i18next';
import { DimOpt, MODULO_LABEL, centrosList, proyectosList, field, usd, hoy, rpc } from './shared';

interface Linea { key: number; cuenta_id: string; glosa: string; debe: number; haber: number; proyecto_id: string; centro_costo_id: string }
let _k = 1;
const nueva = (): Linea => ({ key: _k++, cuenta_id: '', glosa: '', debe: 0, haber: 0, proyecto_id: '', centro_costo_id: '' });

export function Asientos() {
  const { t: tr } = useTranslation('contabilidad');
  const qc = useQueryClient();
  const [vista, setVista] = useState<'nuevo' | 'lista'>('nuevo');
  const [editId, setEditId] = useState<string | null>(null);
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{tr('asientos.titulo')}</h1>
        <div className="flex gap-1 rounded-lg border p-1">
          {([['nuevo', editId ? tr('asientos.tab_editar') : tr('asientos.tab_nuevo')], ['lista', tr('asientos.tab_libro')]] as const).map(([k, t]) => (
            <button key={k} onClick={() => { if (k === 'nuevo' && vista === 'lista') setEditId(null); setVista(k); }} className={`rounded-md px-3 py-1 text-sm ${vista === k ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>{t}</button>
          ))}
        </div>
      </div>
      {vista === 'nuevo'
        ? <Nuevo editId={editId} onSaved={() => { setEditId(null); setVista('lista'); void qc.invalidateQueries({ queryKey: ['cont-asientos'] }); }} />
        : <Lista onEdit={(id) => { setEditId(id); setVista('nuevo'); }} />}
    </div>
  );
}

function Nuevo({ editId, onSaved }: { editId: string | null; onSaved: () => void }) {
  const { t } = useTranslation('contabilidad');
  const { data: partners = [] } = useQuery<DimOpt[]>({ queryKey: ['cont-proyectos'], queryFn: proyectosList });
  const { data: centros = [] } = useQuery<DimOpt[]>({ queryKey: ['cont-centros'], queryFn: centrosList });

  const [fecha, setFecha] = useState(hoy);
  const [glosa, setGlosa] = useState('');
  const [lineas, setLineas] = useState<Linea[]>([nueva(), nueva()]);
  const setLinea = (k: number, patch: Partial<Linea>) => setLineas((ls) => ls.map((l) => (l.key === k ? { ...l, ...patch } : l)));

  // ---- Respaldo/recuperación del asiento en curso (cierre del navegador a medias) ----
  // Solo para asiento NUEVO: en modo edición nunca se pisa lo que ya está guardado.
  interface AsientoBorrador { fecha: string; glosa: string; lineas: Linea[] }
  const { limpiar: limpiarBorrador } = useBorradorPantalla<AsientoBorrador>({
    clave: 'cont_asiento',
    activo: !editId,
    snapshot: { fecha, glosa, lineas },
    hayDatos: !!glosa || lineas.some((l) => l.cuenta_id && (l.debe > 0 || l.haber > 0)),
    onRestaurar: (d) => {
      if (Array.isArray(d.lineas) && d.lineas.length) {
        const maxKey = d.lineas.reduce((m, l) => (Number.isFinite(l.key) ? Math.max(m, l.key) : m), 0);
        _k = Math.max(_k, maxKey + 1);
        setLineas(d.lineas);
      }
      if (d.fecha) setFecha(d.fecha);
      if (d.glosa) setGlosa(d.glosa);
      toast.info(t('asientos.recuperado', { detalle: d.lineas?.length ? t(d.lineas.length === 1 ? 'asientos.recuperado_lineas' : 'asientos.recuperado_lineas_plural', { n: d.lineas.length }) : '' }));
    },
  });

  const { data: edit } = useQuery({ queryKey: ['cont-asiento', editId], enabled: !!editId, queryFn: () => rpc<any>('cont_asiento_obtener', { _id: editId }) });
  useEffect(() => {
    if (!edit) return;
    setFecha(String(edit.fecha).slice(0, 10)); setGlosa(edit.glosa || '');
    setLineas((edit.lineas || []).map((l: { cuenta_id?: string; glosa?: string; debe?: number; haber?: number; proyecto_id?: string; centro_costo_id?: string }) => ({
      key: _k++, cuenta_id: l.cuenta_id || '', glosa: l.glosa || '', debe: Number(l.debe) || 0, haber: Number(l.haber) || 0, proyecto_id: l.proyecto_id || '', centro_costo_id: l.centro_costo_id || '',
    })));
  }, [edit]);

  const totDebe = lineas.reduce((s, l) => s + (Number(l.debe) || 0), 0);
  const totHaber = lineas.reduce((s, l) => s + (Number(l.haber) || 0), 0);
  const dif = Math.round((totDebe - totHaber) * 100) / 100;

  const guardar = useMutation({
    mutationFn: async () => {
      const _lineas = lineas.filter((l) => l.cuenta_id && (l.debe > 0 || l.haber > 0)).map((l) => ({
        cuenta_id: l.cuenta_id, glosa: l.glosa || null, debe: Number(l.debe) || 0, haber: Number(l.haber) || 0, proyecto_id: l.proyecto_id || null, centro_costo_id: l.centro_costo_id || null,
      }));
      if (_lineas.length < 2) throw new Error(t('asientos.err_min_lineas'));
      if (dif !== 0) throw new Error(t('asientos.err_descuadre', { dif: usd(dif) }));
      const _cab = { fecha, glosa: glosa || null, tipo: 'diario' };
      return editId ? rpc('cont_asiento_actualizar', { _id: editId, _cab, _lineas }) : rpc('cont_asiento_crear', { _cab, _lineas });
    },
    onSuccess: () => { toast.success(editId ? t('asientos.actualizado') : t('asientos.registrado')); limpiarBorrador(); onSaved(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{t('asientos.cabecera')}</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div><label className="text-xs font-medium text-muted-foreground">{t('asientos.lbl_fecha')}</label><input type="date" className={field} value={fecha} onChange={(e) => setFecha(e.target.value)} /></div>
          <div className="md:col-span-3"><label className="text-xs font-medium text-muted-foreground">{t('comun.glosa')}</label><input className={field} value={glosa} onChange={(e) => setGlosa(e.target.value)} /></div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <CardTitle className="text-base">{t('asientos.detalle')}</CardTitle>
          <Button size="sm" variant="outline" onClick={() => setLineas((l) => [...l, nueva()])}><Plus className="w-4 h-4 mr-1" /> {t('asientos.linea')}</Button>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-1 pr-2 min-w-64">{t('comun.cuenta')}</th><th className="py-1 pr-2 min-w-40">{t('comun.glosa')}</th><th className="py-1 pr-2 text-right">{t('comun.debe')}</th><th className="py-1 pr-2 text-right">{t('comun.haber')}</th><th className="py-1 pr-2">{t('asientos.col_proyecto')}</th><th className="py-1 pr-2">{t('comun.centro_costo')}</th><th></th>
            </tr></thead>
            <tbody>
              {lineas.map((l) => (
                <tr key={l.key} className="border-b last:border-0">
                  <td className="py-1 pr-2 min-w-64">
                    <BuscadorCuenta value={l.cuenta_id} onChange={(v) => setLinea(l.key, { cuenta_id: v })} allowClear compact />
                  </td>
                  <td className="py-1 pr-2"><input className={field} value={l.glosa} onChange={(e) => setLinea(l.key, { glosa: e.target.value })} /></td>
                  <td className="py-1 pr-2"><input type="number" step="0.01" className={`${field} w-28 text-right`} value={l.debe} onChange={(e) => setLinea(l.key, { debe: Number(e.target.value), haber: 0 })} /></td>
                  <td className="py-1 pr-2"><input type="number" step="0.01" className={`${field} w-28 text-right`} value={l.haber} onChange={(e) => setLinea(l.key, { haber: Number(e.target.value), debe: 0 })} /></td>
                  <td className="py-1 pr-2">
                    <select className={`${field} w-40`} value={l.proyecto_id} onChange={(e) => setLinea(l.key, { proyecto_id: e.target.value })}>
                      <option value="">—</option>
                      {partners.map((p) => <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>)}
                    </select>
                  </td>
                  <td className="py-1 pr-2">
                    <select className={`${field} w-40`} value={l.centro_costo_id} onChange={(e) => setLinea(l.key, { centro_costo_id: e.target.value })}>
                      <option value="">—</option>
                      {centros.map((p) => <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>)}
                    </select>
                  </td>
                  <td className="py-1"><button className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded p-1" onClick={() => setLineas((ls) => ls.filter((x) => x.key !== l.key))}><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr className="border-t font-medium">
              <td className="py-2 pr-2 text-right" colSpan={2}>{t('comun.totales')}</td>
              <td className="py-2 pr-2 text-right">{usd(totDebe)}</td><td className="py-2 pr-2 text-right">{usd(totHaber)}</td><td colSpan={3}></td>
            </tr></tfoot>
          </table>
          <div className={`mt-2 text-right text-sm ${dif === 0 ? 'text-green-600' : 'text-red-600'}`}>{dif === 0 ? t('asientos.cuadrada') : t('asientos.diferencia', { dif: usd(dif) })}</div>
        </CardContent>
      </Card>
      <div className="flex justify-end">
        <Button isLoading={guardar.isPending} disabled={dif !== 0 || totDebe === 0} onClick={() => guardar.mutate()}>{editId ? t('asientos.guardar_cambios') : t('asientos.registrar')}</Button>
      </div>
    </div>
  );
}

interface AsientoRow {
  id: string; numero: number; fecha: string; glosa: string | null; tipo: string; estado: string;
  total_debe: number; modulo: string; origen: string | null;
  // Quién lo hizo y cuánto tardó en registrarse (script 778). La NIA 240 obliga
  // a probar los asientos de diario, y sin estas dos columnas no se puede.
  usuario?: string | null; creado_at?: string | null; dias_captura?: number | null;
}
interface AsientoLineaDet { cuenta_id: string; codigo: string; nombre: string; glosa: string | null; debe: number; haber: number; partner_nombre?: string | null }
interface AsientoDet { id: string; numero: number; fecha: string; glosa: string | null; tipo: string; estado: string; lineas: AsientoLineaDet[] }

function Lista({ onEdit }: { onEdit: (id: string) => void }) {
  const { t } = useTranslation('contabilidad');
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: docs = [], isFetching } = useQuery<AsientoRow[]>({ queryKey: ['cont-asientos'], queryFn: () => rpc<AsientoRow[]>('cont_asientos') });
  const eliminar = useMutation({
    mutationFn: (id: string) => rpc('cont_asiento_eliminar', { _id: id }),
    onSuccess: () => { toast.success(t('asientos.eliminado')); void qc.invalidateQueries({ queryKey: ['cont-asientos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const anular = useMutation({
    mutationFn: (id: string) => rpc('cont_asiento_anular', { _id: id, _motivo: null }),
    onSuccess: () => { toast.success(t('asientos.anulado')); void qc.invalidateQueries({ queryKey: ['cont-asientos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [glosa, setGlosa] = useState('');
  const [num, setNum] = useState('');
  const [vmin, setVmin] = useState('');
  const [vmax, setVmax] = useState('');
  const [verId, setVerId] = useState<string | null>(null);

  const filtered = useMemo(() => docs.filter((d) => {
    const f = String(d.fecha).slice(0, 10);
    if (desde && f < desde) return false;
    if (hasta && f > hasta) return false;
    if (glosa && !(d.glosa ?? '').toLowerCase().includes(glosa.trim().toLowerCase())) return false;
    if (num && !String(d.numero).includes(num.trim())) return false;
    const v = Number(d.total_debe);
    if (vmin !== '' && v < Number(vmin)) return false;
    if (vmax !== '' && v > Number(vmax)) return false;
    return true;
  }), [docs, desde, hasta, glosa, num, vmin, vmax]);

  const hayFiltros = !!(desde || hasta || glosa || num || vmin !== '' || vmax !== '');
  const limpiar = () => { setDesde(''); setHasta(''); setGlosa(''); setNum(''); setVmin(''); setVmax(''); };

  return (
    <div className="space-y-4">
      {dlg.ui}
      <Card>
        <CardContent className="grid grid-cols-2 md:grid-cols-6 gap-3 pt-4">
          <div><label className="text-xs font-medium text-muted-foreground">{t('comun.desde')}</label><input type="date" className={field} value={desde} onChange={(e) => setDesde(e.target.value)} /></div>
          <div><label className="text-xs font-medium text-muted-foreground">{t('comun.hasta')}</label><input type="date" className={field} value={hasta} onChange={(e) => setHasta(e.target.value)} /></div>
          <div className="md:col-span-2"><label className="text-xs font-medium text-muted-foreground">{t('comun.glosa')}</label><input className={field} value={glosa} onChange={(e) => setGlosa(e.target.value)} /></div>
          <div><label className="text-xs font-medium text-muted-foreground">{t('comun.num')}</label><input className={field} value={num} onChange={(e) => setNum(e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs font-medium text-muted-foreground">{t('asientos.valor_min')}</label><input type="number" step="0.01" className={field} value={vmin} onChange={(e) => setVmin(e.target.value)} /></div>
            <div><label className="text-xs font-medium text-muted-foreground">{t('asientos.valor_max')}</label><input type="number" step="0.01" className={field} value={vmax} onChange={(e) => setVmax(e.target.value)} /></div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <CardTitle className="text-base">{t('asientos.libro_n', { n: filtered.length })}</CardTitle>
          {hayFiltros && <button className="text-xs text-primary hover:underline" onClick={limpiar}>{t('asientos.limpiar_filtros')}</button>}
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">{t('comun.num')}</th><th className="py-2 pr-3">{t('comun.fecha')}</th><th className="py-2 pr-3">{t('comun.glosa')}</th><th className="py-2 pr-3">{t('asientos.col_origen')}</th><th className="py-2 pr-3">{t('asientos.col_registro')}</th><th className="py-2 pr-3 text-right">{t('asientos.col_valor')}</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">{t('comun.sin_asientos')}</td></tr>}
              {filtered.map((d) => {
                const manual = d.modulo === 'manual';
                const anulado = d.estado === 'anulado';
                return (
                <tr key={d.id} className={`border-b last:border-0 hover:bg-muted/40 ${anulado ? 'opacity-50 line-through' : ''}`}>
                  <td className="py-2 pr-3 font-mono text-xs">{d.numero}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{String(d.fecha).slice(0, 10)}</td>
                  <td className="py-2 pr-3"><span className="truncate max-w-80 inline-block align-middle">{d.glosa || '—'}</span></td>
                  <td className="py-2 pr-3"><span className={`rounded-full px-2 py-0.5 text-xs ${manual ? 'bg-muted' : 'bg-primary/10 text-primary'}`}>{t(`modulos.${d.modulo}`, { defaultValue: MODULO_LABEL[d.modulo] ?? d.modulo })}</span></td>
                  <td className="py-2 pr-3 whitespace-nowrap">
                    {d.usuario
                      ? <span>{d.usuario}</span>
                      : <span className="text-muted-foreground" title={t('asientos.proceso_tip')}>{t('asientos.proceso')}</span>}
                    {/* Los días entre la fecha contable y el registro: el número que
                        delata el asiento retrodatado. Sólo se muestra cuando duele. */}
                    {Number(d.dias_captura) > 30 && (
                      <span className="ml-1 text-xs text-amber-600" title={t('asientos.dias_tip', { dias: d.dias_captura })}>
                        +{d.dias_captura}d
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap font-medium">{usd(Number(d.total_debe))}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title={t('asientos.ver_imprimir')} onClick={() => setVerId(d.id)}><Eye className="w-4 h-4" /></button>
                    {manual && !anulado ? <>
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted ml-1" title={t('comun.editar')} onClick={() => onEdit(d.id)}><Pencil className="w-4 h-4" /></button>
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 ml-1" title={t('asientos.anular')} onClick={async () => { if (await dlg.confirmar(t('asientos.confirmar_anular', { num: d.numero }), { detalle: t('asientos.detalle_anular'), peligro: true })) anular.mutate(d.id); }}><Ban className="w-4 h-4" /></button>
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title={t('comun.eliminar')} onClick={async () => { if (await dlg.confirmar(t('asientos.confirmar_eliminar', { num: d.numero }), { peligro: true })) eliminar.mutate(d.id); }}><Trash2 className="w-4 h-4" /></button>
                    </> : !manual ? <span className="ml-2 text-xs text-muted-foreground" title={t('asientos.automatico_tip')}>{t('asientos.automatico')}</span> : null}
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {verId && <VerAsiento id={verId} onClose={() => setVerId(null)} />}
    </div>
  );
}

function VerAsiento({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation('contabilidad');
  const { data, isLoading } = useQuery<AsientoDet>({ queryKey: ['cont-asiento-ver', id], queryFn: () => rpc<AsientoDet>('cont_asiento_obtener', { _id: id }) });
  const lineas = data?.lineas ?? [];
  const totDebe = lineas.reduce((s, l) => s + Number(l.debe || 0), 0);
  const totHaber = lineas.reduce((s, l) => s + Number(l.haber || 0), 0);
  return (
    <PanelIntegrado title={data ? t('asientos.comprobante_num', { num: data.numero }) : t('asientos.comprobante')} onClose={onClose} wide>
      <div className="no-print flex justify-end -mt-1 mb-2"><PrintButton /></div>
      <div>
        <div>
          {isLoading || !data ? (
            <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
          ) : (
            <Imprimible titulo={t('asientos.comprobante')} subtitulo={t('asientos.num_corto', { num: data.numero })}>
              <div className="mb-4 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                <div className="flex gap-2"><span className="text-gray-500">{t('asientos.lbl_num')}</span><span className="font-medium">{data.numero}</span></div>
                <div className="flex gap-2"><span className="text-gray-500">{t('asientos.lbl_fecha2')}</span><span className="font-medium">{String(data.fecha).slice(0, 10)}</span></div>
                <div className="flex gap-2"><span className="text-gray-500">{t('asientos.lbl_tipo')}</span><span className="font-medium capitalize">{data.tipo}</span></div>
                <div className="flex gap-2"><span className="text-gray-500">{t('asientos.lbl_estado')}</span><span className="font-medium capitalize">{data.estado}</span></div>
                <div className="col-span-2 flex gap-2"><span className="text-gray-500">{t('asientos.lbl_glosa')}</span><span className="font-medium">{data.glosa || '—'}</span></div>
              </div>
              <table className="w-full border-collapse text-sm">
                <thead><tr className="border-b border-black/40 text-left">
                  <th className="py-1 pr-2">{t('comun.codigo')}</th><th className="py-1 pr-2">{t('comun.cuenta')}</th><th className="py-1 pr-2">{t('comun.glosa')}</th><th className="py-1 pr-2">{t('asientos.col_tercero')}</th>
                  <th className="py-1 pr-2 text-right">{t('comun.debe')}</th><th className="py-1 pr-2 text-right">{t('comun.haber')}</th>
                </tr></thead>
                <tbody>
                  {lineas.map((l, i) => (
                    <tr key={i} className="border-b border-black/10">
                      <td className="py-1 pr-2 font-mono">{l.codigo}</td>
                      <td className="py-1 pr-2">{l.nombre}</td>
                      <td className="py-1 pr-2">{l.glosa || ''}</td>
                      <td className="py-1 pr-2">{l.partner_nombre || ''}</td>
                      <td className="py-1 pr-2 text-right">{Number(l.debe) ? usd(Number(l.debe)) : ''}</td>
                      <td className="py-1 pr-2 text-right">{Number(l.haber) ? usd(Number(l.haber)) : ''}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr className="border-t border-black/40 font-semibold">
                  <td className="py-1 pr-2 text-right" colSpan={4}>{t('comun.totales')}</td>
                  <td className="py-1 pr-2 text-right">{usd(totDebe)}</td>
                  <td className="py-1 pr-2 text-right">{usd(totHaber)}</td>
                </tr></tfoot>
              </table>
            </Imprimible>
          )}
        </div>
      </div>
    </PanelIntegrado>
  );
}
